import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { NextPage } from '../components/NextPage';
import { useLearningData } from '../context/LearningDataContext';
import { useLearningProgress } from '../hooks/useLearningProgress';
import { useToast } from '../components/ui/ToastContainer';
import { BookIcon, VolumeIcon, CheckCircleIcon, PlayIcon, PauseIcon, LightbulbIcon, StarIcon, BrainIcon, PenIcon, HeadphonesIcon, ClockIcon, XIcon } from 'lucide-react';
export function LessonPage() {
  const {
    isArabic
  } = useLanguage();
  const {
    unitId,
    lessonId
  } = useParams<{
    unitId: string;
    lessonId: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    learningData
  } = useLearningData();
  const {
    completeLesson
  } = useLearningProgress();
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [lessonData, setLessonData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [unitData, setUnitData] = useState<any>(null);
  const [courseData, setCourseData] = useState<any>(null);
  useEffect(() => {
    if (!unitId || !lessonId || !learningData) {
      setLoading(false);
      return;
    }
    try {
      const unitIdNum = parseInt(unitId);
      const lessonIdNum = parseInt(lessonId);
      // Check if learningData is an array
      if (!Array.isArray(learningData)) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'بيانات التعلم غير متوفرة' : 'Learning data not available');
        navigate('/learn');
        return;
      }
      // Find course that contains this unit
      const course = learningData.find((course: any) => course.units && Array.isArray(course.units) && course.units.some((unit: any) => unit.id === unitIdNum));
      if (!course) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الدورة' : 'Course not found');
        navigate('/learn');
        return;
      }
      // Find unit
      const unit = course.units.find((unit: any) => unit.id === unitIdNum);
      if (!unit) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الوحدة' : 'Unit not found');
        navigate('/learn');
        return;
      }
      // Check if lessons array exists
      if (!unit.lessons || !Array.isArray(unit.lessons)) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'دروس الوحدة غير متوفرة' : 'Unit lessons not available');
        navigate(`/learn`);
        return;
      }
      // Find lesson
      const lesson = unit.lessons.find((lesson: any) => lesson.id === lessonIdNum);
      if (!lesson) {
        toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'لم يتم العثور على الدرس' : 'Lesson not found');
        navigate(`/learn/unit/${unitId}`);
        return;
      }
      setCourseData(course);
      setUnitData(unit);
      setLessonData(lesson);
    } catch (error) {
      console.error('Error loading lesson data:', error);
      toast.error(isArabic ? 'خطأ في التحميل' : 'Loading Error', isArabic ? 'حدث خطأ أثناء تحميل البيانات' : 'Error loading data');
      navigate('/learn');
    } finally {
      setLoading(false);
    }
  }, [unitId, lessonId, learningData, navigate, toast, isArabic]);
  const playAudio = (text: string) => {
    if (playingAudio === text) {
      window.speechSynthesis.cancel();
      setPlayingAudio(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.onend = () => setPlayingAudio(null);
    setPlayingAudio(text);
    window.speechSynthesis.speak(utterance);
  };
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };
  const checkAnswers = () => {
    setShowResults(true);
    // If all answers are correct, mark lesson as completed
    const allCorrect = lessonData.practice?.every((question: any, index: number) => {
      const selectedAnswer = selectedAnswers[index];
      return selectedAnswer !== undefined && question.options[selectedAnswer].correct;
    });
    if (allCorrect) {
      completeLesson(courseData.id, parseInt(unitId!), parseInt(lessonId!));
      toast.success(isArabic ? 'تم إكمال الدرس' : 'Lesson Completed', isArabic ? 'أحسنت! لقد أكملت هذا الدرس بنجاح' : 'Great job! You have successfully completed this lesson');
    }
  };
  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };
  const getCorrectAnswers = () => {
    if (!lessonData || !lessonData.practice) return 0;
    return lessonData.practice.reduce((count: number, question: any, index: number) => {
      const selectedAnswer = selectedAnswers[index];
      if (selectedAnswer !== undefined && question.options[selectedAnswer].correct) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  if (loading) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3386">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3387">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-3388"></div>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-3389">
            {isArabic ? 'جارِ التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>;
  }
  if (!lessonData) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3390">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3391">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-3392">
            {isArabic ? 'لم يتم العثور على الدرس' : 'Lesson Not Found'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-3393">
            {isArabic ? 'لم نتمكن من العثور على الدرس المطلوب' : "We couldn't find the requested lesson"}
          </p>
          <button onClick={() => navigate('/learn')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3394">
            {isArabic ? 'العودة إلى صفحة التعلم' : 'Back to Learning Page'}
          </button>
        </div>
      </div>;
  }
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3395">
      <Sitemap currentPage={{
      en: `Lesson ${lessonData.id}: ${lessonData.title.en}`,
      ar: `الدرس ${lessonData.id}: ${lessonData.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: `Unit ${unitId}`,
        ar: `الوحدة ${unitId}`
      },
      href: `/learn/unit/${unitId}`
    }]} data-id="element-3396" />
      <div className="max-w-4xl mx-auto" data-id="element-3397">
        <div className="mb-12" data-id="element-3398">
          <div className="flex items-center gap-4 mb-6" data-id="element-3399">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3400">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3401" />
            </div>
            <div data-id="element-3402">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3403">
                {isArabic ? lessonData.title.ar : lessonData.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3404">
                {isArabic ? lessonData.description.ar : lessonData.description.en}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6" data-id="element-3405">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3406">
              <ClockIcon className="w-5 h-5" data-id="element-3407" />
              <span data-id="element-3408">
                {isArabic ? lessonData.durationAr : lessonData.duration}
              </span>
            </div>
            {lessonData.objectives && <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3409">
                <BrainIcon className="w-5 h-5" data-id="element-3410" />
                <span data-id="element-3411">
                  {lessonData.objectives[isArabic ? 'ar' : 'en'].length}{' '}
                  {isArabic ? 'أهداف' : 'objectives'}
                </span>
              </div>}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3412">
          <div className="flex items-center gap-3 mb-6" data-id="element-3413">
            <PenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3414" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3415">
              {isArabic ? 'تمارين' : 'Practice'}
            </h2>
          </div>
          {lessonData.practice ? <div className="space-y-8" data-id="element-3416">
              {lessonData.practice.map((question: any, qIndex: number) => <div key={qIndex} className="space-y-4" data-id="element-3417">
                  <p className="text-lg text-gray-900 dark:text-white" data-id="element-3418">
                    {isArabic ? question.question.ar : question.question.en}
                  </p>
                  <div className="grid grid-cols-1 gap-3" data-id="element-3419">
                    {question.options.map((option: any, oIndex: number) => <button key={oIndex} onClick={() => handleAnswerSelect(qIndex, oIndex)} className={`
                          p-4 rounded-lg text-left transition-colors
                          ${selectedAnswers[qIndex] === oIndex ? showResults ? option.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'}
                        `} data-id="element-3420">
                        <div className="flex justify-between items-center" data-id="element-3421">
                          <div data-id="element-3422">
                            <div className="font-medium" data-id="element-3423">
                              {isArabic ? option.ar : option.en}
                            </div>
                          </div>
                          {showResults && (selectedAnswers[qIndex] === oIndex ? option.correct ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3424" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-3425" /> : null)}
                        </div>
                      </button>)}
                  </div>
                </div>)}
              <div className="mt-6" data-id="element-3426">
                {showResults ? <div data-id="element-3427">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4" data-id="element-3428">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-3429">
                        {isArabic ? 'نتائجك' : 'Your Results'}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300" data-id="element-3430">
                        {isArabic ? `لقد أجبت على ${getCorrectAnswers()} من ${lessonData.practice.length} بشكل صحيح` : `You got ${getCorrectAnswers()} out of ${lessonData.practice.length} correct`}
                      </p>
                    </div>
                    <button onClick={resetPractice} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3431">
                      {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
                    </button>
                  </div> : <button onClick={checkAnswers} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" disabled={Object.keys(selectedAnswers).length < lessonData.practice.length} data-id="element-3432">
                    {isArabic ? 'تحقق من إجاباتك' : 'Check Answers'}
                  </button>}
              </div>
            </div> : <p className="text-gray-600 dark:text-gray-300" data-id="element-3433">
              {isArabic ? 'لا توجد تمارين متاحة لهذا الدرس' : 'No practice exercises available for this lesson'}
            </p>}
        </div>
        {lessonData.tips && <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 mb-8" data-id="element-3434">
            <div className="flex items-center gap-3 mb-6" data-id="element-3435">
              <LightbulbIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3436" />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3437">
                {isArabic ? 'نصائح مفيدة' : 'Helpful Tips'}
              </h2>
            </div>
            <ul className="space-y-4" data-id="element-3438">
              {lessonData.tips.map((tip: any, index: number) => <li key={index} className="flex items-start gap-3" data-id="element-3439">
                  <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-3440" />
                  <span className="text-gray-800 dark:text-gray-200" data-id="element-3441">
                    {isArabic ? tip.ar : tip.en}
                  </span>
                </li>)}
            </ul>
          </div>}
        <NextPage currentPage={lessonData.id} totalPages={unitData?.lessons?.length || 0} nextPageUrl={lessonData.id < (unitData?.lessons?.length || 0) ? `/learn/unit/${unitId}/lesson/${lessonData.id + 1}` : `/learn/unit/${unitId}`} prevPageUrl={lessonData.id > 1 ? `/learn/unit/${unitId}/lesson/${lessonData.id - 1}` : `/learn/unit/${unitId}`} nextTitle={{
        en: 'Next Lesson',
        ar: 'الدرس التالي'
      }} nextDescription={{
        en: 'Continue your learning journey',
        ar: 'واصل رحلة التعلم الخاصة بك'
      }} data-id="element-3442" />
      </div>
    </div>;
}